import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Rocket,
  TrendingUp,
  Target,
  Users,
  BarChart3,
  Calendar,
  CheckCircle,
  Zap,
  Send,
  Lock,
  Sparkles,
  ArrowRight
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import api from '../../lib/axios';

const CopiloteComingSoonPage = () => {
  const { user } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [email, setEmail] = useState(user?.email || '');
  const [submitting, setSubmitting] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Veuillez entrer votre email');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/copilote/newsletter-subscribe', { email });
      setSubscribed(true);
      toast.success('Inscription réussie ! Vous serez parmi les premiers informés.');
    } catch (error) {
      toast.error('Erreur lors de l\'inscription');
    } finally {
      setSubmitting(false);
    }
  };

  const features = [
    {
      icon: Target,
      title: 'Stratégie sur-mesure',
      description: 'Un plan d\'action personnalisé basé sur vos objectifs et votre secteur'
    },
    {
      icon: BarChart3,
      title: 'ROI mesurable',
      description: 'Tableau de bord avec KPIs et suivi en temps réel de vos performances'
    },
    {
      icon: Calendar,
      title: 'Planning automatisé',
      description: 'Calendrier éditorial et planification optimisée de vos actions'
    },
    {
      icon: Users,
      title: 'Accompagnement dédié',
      description: 'Un expert MSC pour piloter et optimiser vos campagnes'
    },
    {
      icon: Zap,
      title: 'Actions automatisées',
      description: 'Automatisation intelligente de vos tâches marketing récurrentes'
    },
    {
      icon: TrendingUp,
      title: 'Croissance accélérée',
      description: 'Résultats concrets avec des objectifs de transformation mesurables'
    }
  ];

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn(
        "transition-all duration-300 p-6 md:p-8 pt-24",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}>
        {/* Hero Section */}
        <div className="relative">
          {/* Blurred background preview */}
          <div className="absolute inset-0 overflow-hidden rounded-3xl">
            <div className="grid grid-cols-3 gap-4 p-8 blur-sm opacity-30">
              {[1,2,3,4,5,6].map(i => (
                <div key={i} className="h-32 bg-gradient-to-br from-orange-200 to-red-200 rounded-xl" />
              ))}
            </div>
          </div>
          
          {/* Main content */}
          <Card className="relative bg-gradient-to-br from-orange-500 via-red-500 to-pink-500 text-white overflow-hidden">
            <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2" />
            
            <CardContent className="relative p-8 md:p-12">
              <div className="flex flex-col md:flex-row items-center gap-8">
                <div className="flex-1 text-center md:text-left">
                  <Badge className="bg-white/20 text-white mb-4">
                    <Sparkles className="w-3 h-3 mr-1" />
                    Prochainement disponible
                  </Badge>
                  
                  <h1 className="text-4xl md:text-5xl font-bold mb-4">
                    COPILOTE
                  </h1>
                  
                  <p className="text-xl md:text-2xl text-white/90 mb-2">
                    Votre cellule d'accompagnement à 360°
                  </p>
                  
                  <p className="text-white/80 mb-6 max-w-xl">
                    Alliant stratégie marketing, création, analyses et plan d'actions 
                    vers des objectifs de transformation mesurables.
                  </p>
                  
                  {!subscribed ? (
                    <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 max-w-md">
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="votre@email.com"
                        className="bg-white/20 border-white/30 text-white placeholder:text-white/60 focus:bg-white/30"
                      />
                      <Button 
                        type="submit"
                        disabled={submitting}
                        className="bg-white text-orange-600 hover:bg-white/90 font-semibold whitespace-nowrap"
                      >
                        {submitting ? (
                          <div className="w-4 h-4 border-2 border-orange-600/30 border-t-orange-600 rounded-full animate-spin" />
                        ) : (
                          <>
                            <Send className="w-4 h-4 mr-2" />
                            M'inscrire
                          </>
                        )}
                      </Button>
                    </form>
                  ) : (
                    <div className="flex items-center gap-3 bg-white/20 rounded-xl p-4 max-w-md">
                      <CheckCircle className="w-6 h-6 text-emerald-300" />
                      <div>
                        <p className="font-semibold">Vous êtes inscrit !</p>
                        <p className="text-sm text-white/80">Vous serez parmi les 50 premiers à en profiter.</p>
                      </div>
                    </div>
                  )}
                  
                  <p className="text-xs text-white/60 mt-3">
                    🎁 50 places disponibles pour le lancement
                  </p>
                </div>
                
                <div className="flex-shrink-0">
                  <div className="relative">
                    <div className="w-48 h-48 md:w-64 md:h-64 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                      <Rocket className="w-24 h-24 md:w-32 md:h-32 text-white" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center animate-bounce">
                      <Lock className="w-6 h-6 text-yellow-800" />
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Features Grid */}
        <div className="mt-12">
          <h2 className="text-2xl font-bold text-[#1a365d] text-center mb-8">
            Ce qui vous attend
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <Card key={idx} className="card card-hover relative overflow-hidden group">
                  {/* Blur overlay */}
                  <div className="absolute inset-0 bg-white/80 backdrop-blur-[2px] z-10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <Badge className="bg-orange-100 text-orange-600">
                      <Lock className="w-3 h-3 mr-1" />
                      Bientôt disponible
                    </Badge>
                  </div>
                  
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-100 to-red-100 rounded-xl flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-orange-600" />
                    </div>
                    <h3 className="font-semibold text-[#1a365d] mb-2">{feature.title}</h3>
                    <p className="text-sm text-[#64748b]">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Bottom CTA */}
        <Card className="card mt-12 bg-gradient-to-r from-slate-900 to-slate-800 text-white">
          <CardContent className="p-8 text-center">
            <h3 className="text-2xl font-bold mb-2">
              Prêt à transformer votre communication ?
            </h3>
            <p className="text-slate-300 mb-6 max-w-xl mx-auto">
              Inscrivez-vous à notre newsletter pour être informé du lancement 
              et faire partie des 50 entreprises qui pourront en profiter en avant-première.
            </p>
            
            {!subscribed && (
              <Button 
                onClick={() => document.querySelector('input[type="email"]')?.focus()}
                className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-semibold"
              >
                <ArrowRight className="w-4 h-4 mr-2" />
                Rejoindre la liste d'attente
              </Button>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default CopiloteComingSoonPage;
